import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { isStaleAccountSessionError } from 'lib/accountSessionState'
import { ListUsersParams, UserServiceV4 } from 'services'
import debounce from 'lodash.debounce'
import { useAuth } from '@frontegg/react'
import theme from 'theme'

/* Utils */
import { User } from '../../../../models'
import { breadcrumbs, DEMO_ROLE_NAME, hasPermission, Permission } from '../../../../lib/utils'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { modalPropsSelector, ModalType, openModal } from '../../../../store/modals/modalSlice'
import { closeToast, openToast, toastPropsSelector, ToastVariant } from '../../../../store/toasts/toastSlice'

/* Components */
import {
  EmptyRow,
  Row,
  TableWrapper,
  TableHeader,
  UserManagementContainer,
  UserManagementWrapper
} from './user-management.styles'
import { ThemeButton } from '../../../components/buttons'
import {
  CheckboxChecked,
  CheckboxIndeterminate,
  CheckboxUnchecked,
  DeleteIcon,
  PaginationArrowRight,
  PlusIcon
} from '../../../components/svg'
import SearchUsers from './components/SearchUsers'
import HeaderComponent from '../../../components/header/HeaderComponent'
import ToolbarComponent from '../../../components/header/ToolbarComponent'
import ContentWrapper from '../../../components/header/ContentWrapper'
import { PermissionTooltip } from 'ui/components/tooltip'

const PAGINATION_LIMIT = 50

const UserManagement: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const toastProps = useAppSelector(toastPropsSelector)
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [limit, setLimit] = useState(PAGINATION_LIMIT)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [hasNext, setHasNext] = useState(users.length >= 20)
  const [selected, setSelected] = useState<User[]>([])
  const [searchedOptions, setSearchedOptions] = useState<string[]>(
    JSON.parse(localStorage.getItem('USERS_SEARCH_TERMS') || '[]')
  )
  const [loading, setLoading] = useState(false)
  const [canInvite, setCanInvite] = useState<{ allowed: boolean; reason?: string } | null>(null)
  const { user } = useAuth()
  // The effect below re-runs whenever a toast opens, so without this a failing load loops the toast.
  const loadErrorShownRef = useRef(false)

  const fetchUsers = async () => {
    const { limit, total, users } = await UserServiceV4.listUsers()
    setTotal(total)
    setLimit(limit)
    setHasNext(total > limit)
    setHasPrevious(false)
    setUsers(users)
    setAllUsers(users)
    loadErrorShownRef.current = false
  }

  const fetchCanInvite = async () => {
    try {
      setCanInvite(await UserServiceV4.getCanInvite())
    } catch {
      setCanInvite({ allowed: true })
    }
  }

  useEffect(() => {
    fetchUsers().catch((e) => {
      if (isStaleAccountSessionError(e) || loadErrorShownRef.current) return
      loadErrorShownRef.current = true
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed loading users',
            description: (e as any).response?.data?.message ?? 'There was a problem while loading the users list.'
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    })
    fetchCanInvite()
  }, [modalProps.props, toastProps.props])

  const handleInviteUser = async () => {
    dispatch(openModal({ type: ModalType.inviteUser }))
  }

  const handleCheckAll = () => {
    if (selected.length === users.length) {
      setSelected([])
    } else {
      setSelected(users)
    }
  }

  const handlePrevious = () => {
    if (page > 0) {
      setPage((page) => page - 1)
    }
    setPage(0)
  }

  const handleNext = async () => {
    const nextPage = page + 1
    setPage(nextPage)
    const res = await UserServiceV4.listUsers({ page: nextPage, limit })
    setLimit(res.limit)
    setTotal(res.total)
    setUsers(res.users)
    setHasNext(res.total > res.limit)
    setHasPrevious(nextPage > 0)
  }

  const handleCheckboxChange = (e: boolean, n: User) => {
    let c = [...selected]
    if (e) {
      c.push(n)
    } else {
      c = c.filter((k) => k.name !== n.name)
    }
    setSelected(c)
  }

  const deleteUserCallback = () => {
    setSelected([])
  }

  const handleDeleteUsers = () => {
    dispatch(openModal({ type: ModalType.deleteUsers, props: { users: selected, callback: deleteUserCallback } }))
  }

  const handleSearch = async (event: SyntheticEvent, value: string) => {
    setLoading(true)
    const params: ListUsersParams = { page, limit, search: value }

    /* Fallback */
    const filtered = allUsers.filter((n) => {
      if (n.email.toLowerCase().includes(value.toLowerCase())) {
        return n
      }
      if (n.name.toLowerCase().includes(value.toLowerCase())) {
        return n
      }
      if (n.role && n.role.toLowerCase().includes(value.toLowerCase())) {
        return n
      }
      if (n.verified && value.toLowerCase().includes('status')) {
        return n
      }
      if (!n.verified && (value.toLowerCase().includes('pending') || value.toLowerCase().includes('approval'))) {
        return n
      }
      return null
    })

    try {
      const res = await UserServiceV4.listUsers(params)
      setLimit(res.limit)
      setTotal(res.total)
      setUsers(res.users)
      setHasNext(res.total > res.limit)
      setHasPrevious(page > 0)

      const newSearched = [...searchedOptions]
      if (!newSearched.includes(value)) {
        newSearched.push(value)
        setSearchedOptions(newSearched)
      }
      localStorage.setItem('USERS_SEARCH_TERMS', JSON.stringify(newSearched))
    } catch (e) {
      console.error(e)
      setUsers(filtered)
    } finally {
      setLoading(false)
    }
  }

  const debouncedHandleSearch = useMemo(() => debounce(handleSearch, 500), [allUsers])

  const handlePasswordReset = async () => {
    if (
      !hasPermission(Permission.UsersSelfPasswordUpdate, user) ||
      !hasPermission(Permission.UsersPasswordUpdate, user)
    ) {
      return
    }
    if (selected.length > 0) {
      try {
        await UserServiceV4.resetPasswordForUsers(selected.map((n) => n.id))

        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: {
              text: `Reset Password Link${selected.length > 1 ? 's' : ''} Sent`,
              description: `Password reset link${selected.length > 1 ? 's' : ''} sent to ${selected.length} user${
                selected.length > 1 ? 's' : ''
              }`
            }
          })
        )
      } catch (e) {
        dispatch(
          openToast({
            variant: ToastVariant.error,
            props: {
              text: 'Failed sending link',
              description:
                (e as any).response?.data.message ?? 'There was a problem while sending your password reset link.'
            }
          })
        )
      } finally {
        setTimeout(() => {
          dispatch(closeToast())
        }, 5000)
      }
    }
  }

  const handleOpenProfile = (rowUser: User) => {
    if (user && user.email === rowUser.email) {
      const loggedInUser: User = {
        created_at: (user as any).createdAt || '',
        email: user.email,
        id: user.id,
        name: user.name || '',
        phone_number: user.phoneNumber ?? null,
        profile_picture_url: user.profilePictureUrl || '',
        workspace_id: user.tenantId || '',
        verified: user.verified || false,
        last_login_at: (user as any).lastLogin ?? null,
        role: user.roles.length > 0 ? user.roles.map((r) => r.name).join(', ') : DEMO_ROLE_NAME
      }
      dispatch(openModal({ type: ModalType.userProfile, props: { user: loggedInUser } }))
    } else {
      dispatch(openModal({ type: ModalType.userProfile, props: { user: rowUser } }))
    }
  }

  return (
    <Box sx={{ "html[data-theme='light'] & > .header-container": { background: theme.colors.surfaceContainerBackground } }}>
      <HeaderComponent breadcrumbs={breadcrumbs.userManagement} />

      <ContentWrapper line={2} paddingBottom={80}>
        <UserManagementContainer>
          <ToolbarComponent className="flush-toolbar">
            <UserManagementWrapper>
              <SearchUsers options={searchedOptions} onInputChange={debouncedHandleSearch} loading={loading} />
              <ThemeButton
                themevariant="primary"
                font="big"
                onClick={handleInviteUser}
                disabled={canInvite === null || !canInvite.allowed || !hasPermission(Permission.UsersInvite, user)}
              >
                <>
                  {canInvite === null ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PlusIcon />}
                  Add User
                </>
              </ThemeButton>
            </UserManagementWrapper>
          </ToolbarComponent>
          {canInvite && !canInvite.allowed && (
            <Box
              sx={{
                padding: `${theme.spacing.medium}px`,
                marginBottom: `${theme.spacing.medium}px`,
                borderRadius: theme.attributes.borderRadius.mediumSmall,
                // border: `1px solid ${theme.colors.controlBorder}`,
                background: theme.colors.strokePrimary
              }}
            >
              <Typography
                sx={{
                  color: theme.colors.cta
                }}
              >
                New users are added automatically when they sign in with your SSO provider. Direct invites are disabled for accounts using SSO.
              </Typography>
            </Box>
          )}
          <TableWrapper>
            <TableHeader>
              <Box className="header-actions">
                <IconButton className="user-checkbox" onClick={handleCheckAll}>
                  {selected.length > 0 && selected.length < users.length ? (
                    <CheckboxIndeterminate className="checkbox-selected" />
                  ) : selected.length > 0 && selected.length === users.length ? (
                    <CheckboxChecked className="checkbox-selected" />
                  ) : (
                    <CheckboxUnchecked />
                  )}
                </IconButton>
                {selected.length > 0 && (!canInvite || canInvite.allowed) && (
                  <Button
                    className="password-reset"
                    onClick={handlePasswordReset}
                    disabled={!hasPermission(Permission.UsersPasswordUpdate, user)}
                  >
                    <SendIcon />
                    Send Password Reset Link
                  </Button>
                )}
                {selected.length > 0 && (
                  <>
                    {!hasPermission(Permission.UsersDelete, user) ? (
                      <PermissionTooltip
                        PopperProps={{
                          sx: {
                            marginTop: `-${theme.spacing.sm}px !important`
                          }
                        }}
                        title={
                          <>
                            <Box className="tooltip-wrapper-permissions">
                              <Typography className="tooltip-text">Insufficient permissions</Typography>
                            </Box>
                          </>
                        }
                      >
                        <Button className="delete-user disabled" disableTouchRipple>
                          <DeleteIcon />
                          Delete {selected.length > 1 ? 'Users' : 'User'}
                        </Button>
                      </PermissionTooltip>
                    ) : (
                      <Button
                        className="delete-user"
                        onClick={handleDeleteUsers}
                        disabled={!hasPermission(Permission.UsersDelete, user)}
                      >
                        <DeleteIcon />
                        Delete {selected.length > 1 ? 'Users' : 'User'}
                      </Button>
                    )}
                  </>
                )}
              </Box>
              <Box className="pagination-actions">
                <Typography className="pagination-text">
                  {page + 1}-{total <= users.length ? total : limit} of {limit}
                </Typography>
                <IconButton onClick={handlePrevious} disabled={!hasPrevious} className="arrow-button">
                  <PaginationArrowRight style={{ transform: 'rotate(180deg)' }} />
                </IconButton>
                <IconButton onClick={handleNext} disabled={!hasNext} className="arrow-button">
                  <PaginationArrowRight />
                </IconButton>
              </Box>
            </TableHeader>
            {users.length > 0 && (
              <Box sx={{ paddingBottom: '40px' }}>
                {users.map((user: User, index) => {
                  return (
                    <Row key={index} userrole={user.role ? user.role.toLowerCase() : ''}>
                      {selected.findIndex((e) => user.email === e.email) > -1 ? (
                        <IconButton className="user-checkbox" onClick={() => handleCheckboxChange(false, user)}>
                          <CheckboxChecked className="checkbox-selected" />
                        </IconButton>
                      ) : (
                        <IconButton className="user-checkbox" onClick={() => handleCheckboxChange(true, user)}>
                          <CheckboxUnchecked />
                        </IconButton>
                      )}

                      <Typography className="name" onClick={() => handleOpenProfile(user)}>
                        {user.name}
                      </Typography>
                      <Typography className="email" onClick={() => handleOpenProfile(user)} data-private>
                        {user.email}
                      </Typography>

                      <Box
                        className="actions-wrapper"
                        onClick={() => handleOpenProfile(user)}
                        sx={!user.role ? { display: 'flex', justifyContent: 'flex-end' } : null}
                      >
                        {user.role && (
                          <Box className="permissions-container">
                            <Box className="permissions-title">
                              {user.role === 'cpi_manager' ? 'Manager' : user.role}
                            </Box>
                          </Box>
                        )}
                        <Box
                          className="status-container"
                          onClick={() => handleOpenProfile(user)}
                          sx={!user.role ? { width: 'fit-content !important', marginRight: '60px !important' } : null}
                        >
                          <Box className="user-status">
                            {!user.role ? 'Pending Approval' : user.verified ? 'Active' : 'Pending Approval'}
                          </Box>
                        </Box>
                      </Box>
                    </Row>
                  )
                })}
              </Box>
            )}
            {allUsers.length > 0 && !users.length && (
              <EmptyRow>
                <Typography className="title">No results were found.</Typography>
                <Typography className="text">Try a new search to get started.</Typography>
              </EmptyRow>
            )}
          </TableWrapper>
        </UserManagementContainer>
      </ContentWrapper>
    </Box>
  )
}

export default UserManagement
